import { Divider, Stack } from '@mui/material';
import { z } from 'zod';

import { api } from 'common';

import { OrderJobParamsFormFields, OrderJobParamsSchema } from './Common/Orders';
import { PayorParamsFormFields, PayorParamsSchema } from './Common/Payor';
import { type AsynchronousJobVariant, NullableStringSchema, OptionalStringSchema } from './common';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';

import JobKind = api.RpaJob.Kind;

export const ExtractAuthorizationStatusVariant: AsynchronousJobVariant = {
  schema: z.object({
    jobKind: z.literal(JobKind.ExtractAuthorizationStatus),
    payload: OrderJobParamsSchema.merge(PayorParamsSchema).merge(
      z.object({
        trackingNumber: NullableStringSchema,
        cptCode: NullableStringSchema,
        patientAddress: z.object({
          address: NullableStringSchema,
          city: NullableStringSchema,
          state: NullableStringSchema,
          zip: NullableStringSchema,
        }),
        referredByProvider: z.object({
          firstName: NullableStringSchema,
          lastName: NullableStringSchema,
          npi: NullableStringSchema,
          phoneNumber: NullableStringSchema,
        }),
        referredByDepartment: OptionalStringSchema,
        referredToDepartment: OptionalStringSchema,
      })
    ),
  }),
  Config: () => {
    return (
      <Stack spacing={2}>
        <BlockValueTemplateField
          fieldName="config.payload.trackingNumber"
          label="Tracking Number"
          helperText="Enter the reference to the Tracking Number. e.g. `{{authorization.trackingNumber}}`"
          placeholder="e.g. {{authorization.trackingNumber}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.cptCode"
          label="CPT Code"
          helperText="Enter the reference to the CPT Code. e.g. `{{authorization.cptCode}}`"
          placeholder="e.g. {{authorization.cptCode}}"
        />
        <Divider />
        <BlockValueTemplateField
          fieldName="config.payload.patientAddress.address"
          label="Patient Address"
          helperText="Enter the reference to the practice's address."
          placeholder="e.g. {{patientAddress.address}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientAddress.city"
          label="Patient City"
          helperText="Enter the reference to the practice's city."
          placeholder="e.g. {{patientAddress.city}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientAddress.state"
          label="Patient State"
          helperText="Enter the reference to the practice's state."
          placeholder="e.g. {{patientAddress.state}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientAddress.zip"
          label="Patient ZIP Code"
          helperText="Enter the reference to the practice's ZIP code."
          placeholder="e.g. {{patientAddress.zip}}"
        />
        <Divider />
        <BlockValueTemplateField
          fieldName="config.payload.referredByProvider.firstName"
          label="Referred By Provider First Name"
          helperText="Enter the reference to the referring provider's first name."
          placeholder="e.g. {{order.data.referredByProvider.firstName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredByProvider.lastName"
          label="Referred By Provider Last Name"
          helperText="Enter the reference to the referring provider's last name."
          placeholder="e.g. {{order.data.referredByProvider.lastName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredByProvider.npi"
          label="Referred By Provider NPI"
          helperText="Enter the reference to the referring provider's NPI."
          placeholder="e.g. {{order.data.referredByProvider.npi}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredByProvider.phoneNumber"
          label="Referred By Provider Phone Number"
          helperText="Enter the reference to the referring provider's phone number."
          placeholder="e.g. {{order.data.referredByProvider.phoneNumber}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredByDepartment"
          label="Referred By Department"
          helperText="Enter the reference to the referring department."
          placeholder="e.g. {{order.data.referredByDepartment}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredToDepartment"
          label="Referred To Department"
          helperText="Enter the reference to the department being referred to."
          placeholder="e.g. {{order.data.referredToDepartment}}"
        />
        <Divider />
        <OrderJobParamsFormFields />
        <Divider />
        <PayorParamsFormFields />
      </Stack>
    );
  },
};
